import { useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { clsx } from '../../ds/clsx';
import { Button } from '../../ds/components';
import { useIsMobile } from '../../ds/hooks';
import { errorMessage } from '../../lib/api';
import { bookAppointmentHref, isNextStepRequired, useTasks } from '../tasks/api';
import { longDay } from '../tasks/dates';
import { NextStepDialog, type NextStep } from '../tasks/NextStepDialog';
import { SnoozePopover, type SnoozeValue } from '../tasks/SnoozePopover';
import { entryTitle, useAdvanceQueue, useCurrentQueue, useEndQueue, usePauseQueue } from './api';
import styles from './QueueBar.module.css';

const SESSION_ROUTE = '/tasks/queue/session';

/**
 * Task-Queue 04: the navy focus-mode header the shell shows on every route except the session page while
 * a queue is running or paused — type badge, title, due and reference, „X von Y", and „Verschieben" /
 * „Abgeschlossen und weiter" / „Queue beenden" (paused: „Fortsetzen"). The primary action of the session
 * page opens the contact record and the session continues here.
 */
export function QueueBar() {
  const location = useLocation();
  const onSessionPage = location.pathname.startsWith(SESSION_ROUTE);
  const current = useCurrentQueue(!onSessionPage);
  const state = current.data;
  if (onSessionPage || !state || state.session === null) return null;
  return <QueueBarBody />;
}

function QueueBarBody() {
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const current = useCurrentQueue();
  const tasks = useTasks(false);
  const advance = useAdvanceQueue();
  const pause = usePauseQueue();
  const end = useEndQueue();
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [nextStepOpen, setNextStepOpen] = useState(false);
  const [nextStepError, setNextStepError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const snoozeTrigger = useRef<HTMLButtonElement>(null);

  const state = current.data;
  if (!state || state.session === null) return null;
  const session = state.session;
  const entry = state.entry;
  const task = entry?.task ?? null;
  const paused = session.status === 'paused';
  const busy = advance.isPending || pause.isPending || end.isPending;
  const quickPicks = tasks.data?.quickPicks ?? [];

  const badge = task
    ? task.typeLabel
    : entry?.kind === 'appointment'
      ? 'Termin'
      : entry
        ? 'Kontakt'
        : 'Queue';
  const title = entry ? entryTitle(entry) : 'Die Queue ist abgearbeitet.';
  const dueLine = task
    ? task.due.overdue && task.due.on
      ? `${task.due.label} · fällig ${longDay(task.due.on).slice(0, 6)}${task.due.time ? `, ${task.due.time}` : ''}`
      : `fällig ${task.due.label}`
    : entry?.appointment
      ? entry.appointment.label
      : '';
  const metaLine = task
    ? [`Priorität ${task.priority.toUpperCase()}`, task.reference].filter(Boolean).join(' · ')
    : (entry?.contact?.roleLine ?? '');

  async function runDone(nextStep?: NextStep) {
    setError(null);
    try {
      const result = await advance.mutateAsync({
        id: session.id,
        body: { result: 'done', ...(nextStep ? { taskCompletion: { nextStep } } : {}) },
      });
      setNextStepOpen(false);
      setNextStepError(null);
      if (result.next === 'book_appointment' && task?.contactId) {
        void navigate(bookAppointmentHref(task.contactId, result.createdTaskId));
      }
    } catch (caught) {
      if (isNextStepRequired(caught) && nextStep === undefined) {
        setNextStepError(null);
        setNextStepOpen(true);
        return;
      }
      if (nextStep) setNextStepError(errorMessage(caught));
      else setError(errorMessage(caught));
    }
  }

  function runLater(value: SnoozeValue) {
    setError(null);
    advance.mutate(
      { id: session.id, body: { result: 'later', later: value } },
      { onSuccess: () => setSnoozeOpen(false), onError: (caught) => setError(errorMessage(caught)) },
    );
  }

  function runEnd() {
    // The bar unmounts as soon as the session is cleared, so the navigation cannot hang on the
    // mutation's mount-bound callbacks; the promise outlives the component.
    end
      .mutateAsync(session.id)
      .then((summary) => navigate(`${SESSION_ROUTE}?summary=${summary.sessionId}`))
      .catch((caught: unknown) => setError(errorMessage(caught)));
  }

  function togglePause() {
    pause.mutate(
      { id: session.id, action: paused ? 'resume' : 'pause' },
      { onError: (caught) => setError(errorMessage(caught)) },
    );
  }

  return (
    <div className={clsx(styles.bar, mobile && styles.barMobile)} role="region" aria-label="Laufende Queue">
      <span className={styles.badge}>{badge}</span>
      <div className={styles.text}>
        <Link to={SESSION_ROUTE} className={styles.title}>
          {title}
        </Link>
        <div className={styles.meta}>
          {dueLine ? <span className={styles.due}>{dueLine}</span> : null}
          {metaLine ? <span className={styles.reference}>{metaLine}</span> : null}
          {paused ? <span className={styles.pausedTag}>pausiert</span> : null}
          {error ? <span className={styles.error}>{error}</span> : null}
        </div>
      </div>
      <div className={styles.position}>
        <div className={styles.positionLabel}>Queue</div>
        <div className={styles.positionValue}>
          {state.progress.position} von {state.progress.total}
        </div>
      </div>
      <div className={styles.actions}>
        {paused ? (
          <Button size="large" className={styles.primary} loading={pause.isPending} onClick={togglePause}>
            Fortsetzen
          </Button>
        ) : (
          <>
            {task ? (
              <span className={styles.snoozeAnchor}>
                <Button
                  ref={snoozeTrigger}
                  variant="ghostInverse"
                  size="large"
                  className={styles.ghost}
                  aria-haspopup="dialog"
                  aria-expanded={snoozeOpen}
                  disabled={busy}
                  onClick={() => setSnoozeOpen((value) => !value)}
                >
                  Verschieben
                </Button>
                {snoozeOpen ? (
                  <SnoozePopover
                    placement="end"
                    quickPicks={quickPicks}
                    initialOn={task.due.on}
                    initialTime={task.due.time}
                    pending={advance.isPending}
                    error={error}
                    triggerRef={snoozeTrigger}
                    onCancel={() => setSnoozeOpen(false)}
                    onSubmit={runLater}
                  />
                ) : null}
              </span>
            ) : null}
            {entry ? (
              <Button
                size="large"
                className={styles.primary}
                loading={advance.isPending}
                onClick={() => void runDone()}
              >
                Abgeschlossen und weiter
              </Button>
            ) : null}
          </>
        )}
        <Button
          variant="ghostInverse"
          size="large"
          className={clsx(styles.ghost, styles.ghostMuted)}
          disabled={busy}
          onClick={runEnd}
        >
          Queue beenden
        </Button>
      </div>
      <NextStepDialog
        open={nextStepOpen}
        contactName={entry?.contact?.name ?? task?.contact?.name ?? task?.reference ?? ''}
        onBack={() => {
          setNextStepOpen(false);
          setNextStepError(null);
        }}
        onSubmit={(step) => runDone(step)}
        pending={advance.isPending}
        error={nextStepError}
      />
    </div>
  );
}
